/**
 * Цвет текста и выделение фоном в markdown-полях.
 *
 * Стандартный markdown цвета не знает, поэтому цвет хранится короткими
 * HTML-тегами с ключом из палитры (`@lens/shared` MARKDOWN_*_COLORS):
 *   <span data-color="red">текст</span>
 *   <mark data-bg="yellow">текст</mark>
 * Произвольный CSS в значение не попадает — только ключ, оттенок по теме
 * задаёт markdown-field.css.
 *
 * Редактор: две марки Tiptap; tiptap-markdown пишет их тегами при сохранении,
 * а при загрузке markdown-it (html: true) отдаёт теги как HTML, и parseHTML
 * марки их подхватывает.
 * Просмотр: react-markdown без rehype-raw сырой HTML выкидывает, поэтому
 * remarkColorTags собирает пары открывающий/закрывающий тег в узлы span/mark/u.
 * Любой другой HTML так и остаётся выкинутым.
 */
import { Mark, mergeAttributes } from '@tiptap/react';
import { isMarkdownHighlightColor, isMarkdownTextColor } from '@lens/shared';

interface MarkdownSerializerState {
  write: (s: string) => void;
}

export const TextColor = Mark.create({
  name: 'textColor',

  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-color'),
        renderHTML: (attrs: { color?: string | null }) => (attrs.color ? { 'data-color': attrs.color } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-color]', getAttrs: (el: HTMLElement) => (isMarkdownTextColor(el.getAttribute('data-color')) ? null : false) }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: (_state: MarkdownSerializerState, mark: { attrs: { color?: string } }) => `<span data-color="${mark.attrs.color ?? ''}">`,
          close: '</span>',
          expelEnclosingWhitespace: true,
        },
        parse: {},
      },
    };
  },
});

export const Highlight = Mark.create({
  name: 'highlight',

  addAttributes() {
    return {
      bg: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-bg'),
        renderHTML: (attrs: { bg?: string | null }) => (attrs.bg ? { 'data-bg': attrs.bg } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'mark[data-bg]', getAttrs: (el: HTMLElement) => (isMarkdownHighlightColor(el.getAttribute('data-bg')) ? null : false) }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['mark', mergeAttributes(HTMLAttributes), 0];
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: (_state: MarkdownSerializerState, mark: { attrs: { bg?: string } }) => `<mark data-bg="${mark.attrs.bg ?? ''}">`,
          close: '</mark>',
          expelEnclosingWhitespace: true,
        },
        parse: {},
      },
    };
  },
});

// ---------------------------------------------------------------------------
// Просмотр: remark-плагин для react-markdown.

interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: { hName?: string; hProperties?: Record<string, string> };
}

interface OpenTag {
  tag: 'span' | 'mark' | 'u';
  props: Record<string, string>;
}

const OPEN_RE = /^<(span|mark|u)(?:\s+data-(color|bg)="([a-z]+)")?\s*>$/;
const CLOSE_RE = /^<\/(span|mark|u)\s*>$/;

function parseOpen(value: string): OpenTag | null {
  const m = OPEN_RE.exec(value.trim());
  if (!m) return null;
  const tag = m[1] as OpenTag['tag'];
  const attr = m[2];
  const key = m[3];
  if (tag === 'span') return attr === 'color' && isMarkdownTextColor(key) ? { tag, props: { dataColor: key } } : null;
  if (tag === 'mark') return attr === 'bg' && isMarkdownHighlightColor(key) ? { tag, props: { dataBg: key } } : null;
  return attr ? null : { tag, props: {} };
}

function wrapColorTags(children: MdNode[]): MdNode[] {
  const root: MdNode[] = [];
  const stack: { open: OpenTag; raw: MdNode; children: MdNode[] }[] = [];
  const out = (): MdNode[] => stack[stack.length - 1]?.children ?? root;

  for (const child of children) {
    if (child.type === 'html' && typeof child.value === 'string') {
      const open = parseOpen(child.value);
      if (open) {
        stack.push({ open, raw: child, children: [] });
        continue;
      }
      const close = CLOSE_RE.exec(child.value.trim());
      const top = stack[stack.length - 1];
      if (close && top && top.open.tag === close[1]) {
        stack.pop();
        out().push({
          type: 'lensInline',
          children: top.children,
          data: { hName: top.open.tag, hProperties: top.open.props },
        });
        continue;
      }
    }
    out().push(child);
  }
  // Незакрытые теги — возвращаем как было (сырой html просмотр всё равно не покажет).
  while (stack.length > 0) {
    const frame = stack.pop()!;
    out().push(frame.raw, ...frame.children);
  }
  return root;
}

function visit(node: MdNode): void {
  if (!node.children) return;
  for (const child of node.children) visit(child);
  node.children = wrapColorTags(node.children);
}

export function remarkColorTags() {
  return (tree: MdNode): void => visit(tree);
}
